import type { Episode } from './clinical/episode'

const DB_NAME = 'diabetes-emergency-tool'
const DB_VERSION = 2
const SELECTED_EPISODE_KEY = 'diabetes-emergency-tool:selected-episode'
const NEW_CASE_SENTINEL = '__new_case__'

export type StoredEpisode = Episode & { updatedAt?: string }

async function openDb() {
  const request = indexedDB.open(DB_NAME, DB_VERSION)
  return await new Promise<IDBDatabase>((resolve, reject) => {
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains('evaluations')) {
        db.createObjectStore('evaluations', { keyPath: 'id' })
      }
      if (!db.objectStoreNames.contains('episodes')) {
        db.createObjectStore('episodes', { keyPath: 'id' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function readSelection(): string | null {
  try {
    return localStorage.getItem(SELECTED_EPISODE_KEY)
  } catch {
    return null
  }
}

function writeSelection(value: string) {
  try {
    localStorage.setItem(SELECTED_EPISODE_KEY, value)
  } catch {
    // IndexedDB remains the source of truth if localStorage is unavailable.
  }
}

export function markNewCaseSelected() {
  writeSelection(NEW_CASE_SENTINEL)
}

export function markEpisodeSelected(id: string) {
  writeSelection(id)
}

export async function saveLocalEvaluation<T>(payload: T) {
  const db = await openDb()
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction('evaluations', 'readwrite')
    tx.objectStore('evaluations').put({ id: crypto.randomUUID(), createdAt: new Date().toISOString(), payload })
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
  db.close()
}

export async function saveEpisode(episode: Episode) {
  const db = await openDb()
  const updatedAt = new Date().toISOString()
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction('episodes', 'readwrite')
    tx.objectStore('episodes').put({ ...episode, updatedAt })
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
  db.close()
  markEpisodeSelected(episode.id)
}

export async function listEpisodes(): Promise<StoredEpisode[]> {
  const db = await openDb()
  const records = await new Promise<StoredEpisode[]>((resolve, reject) => {
    const tx = db.transaction('episodes', 'readonly')
    const request = tx.objectStore('episodes').getAll()
    request.onsuccess = () => resolve(request.result || [])
    request.onerror = () => reject(request.error)
  })
  db.close()

  return records.sort(
    (a, b) => new Date(b.updatedAt || b.startedAt).getTime() - new Date(a.updatedAt || a.startedAt).getTime()
  )
}

export async function loadEpisodeById(id: string): Promise<StoredEpisode | null> {
  const db = await openDb()
  const record = await new Promise<StoredEpisode | undefined>((resolve, reject) => {
    const tx = db.transaction('episodes', 'readonly')
    const request = tx.objectStore('episodes').get(id)
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
  db.close()
  return record || null
}

export async function loadLatestEpisode(): Promise<Episode | null> {
  const records = await listEpisodes()
  const active = records.filter((record) => record.status !== 'closed')
  return active[0] || null
}

export async function loadSelectedEpisode(): Promise<Episode | null> {
  const selected = readSelection()
  if (selected === NEW_CASE_SENTINEL) return null

  if (selected) {
    const episode = await loadEpisodeById(selected)
    if (episode) return episode
  }

  const latest = await loadLatestEpisode()
  if (latest) markEpisodeSelected(latest.id)
  return latest
}
